import React, { useMemo } from 'react';

import { Flex, xcss } from '@atlaskit/primitives';

import { BoardColumnsSvg } from '../../onboarding-wizard/wizard/assets/board-columns-svg';
import { Injections, ProjectSvg } from '../../onboarding-wizard/wizard/assets/project-svg';
import { AgentAvatar } from '../left-hand-side/agent-avatar';
import { CodingAgentId } from '../types';

const DEFAULT_COLUMNS = ['To Do', 'In Progress', 'Done'];

/**
 * Number of "In Progress" board cards that can display an assigned agent
 * avatar (matches the Figma design, node 8494-19175).
 */
const ASSIGNABLE_CARD_COUNT = 4;

/** Size (px) of the agent avatar shown on a board card. */
const BOARD_AVATAR_SIZE = 16;

export interface RightHandSideCodingAgentChoiceProps {
	readonly projectName?: string;
	/**
	 * Coding agents the user selected on the left-hand side. Their avatars
	 * are distributed across the "In Progress" board cards so the board
	 * reflects the agents assigned to the work.
	 */
	readonly selectedAgents?: CodingAgentId[];
}

/**
 * TWCG-1935: right-hand side of the coding-agent-choice screen.
 *
 * Mirrors the wizard wireframe by rendering the same Jira project
 * mock SVG used by `page-board-columns` so the modal feels
 * continuous across the onboarding flow. When the user selects coding
 * agents on the left-hand side, their avatars appear on the "In Progress"
 * board cards: each selected agent fills one card in order, so selecting a
 * single agent only populates the first card.
 */
export const RightHandSideCodingAgentChoice: React.FC<RightHandSideCodingAgentChoiceProps> = ({
	projectName,
	selectedAgents = [],
}) => {
	const injections: Injections = useMemo(() => {
		const assignedAgents = Array.from({ length: ASSIGNABLE_CARD_COUNT }, (_, index) => {
			const agentId = selectedAgents[index];
			if (!agentId) {
				return undefined;
			}
			return <AgentAvatar key={`${agentId}-${index}`} agentId={agentId} size={BOARD_AVATAR_SIZE} />;
		});
		return {
			svgBody: BoardColumnsSvg({
				columnNames: DEFAULT_COLUMNS,
				assignedAgents,
			}),
		};
	}, [selectedAgents]);

	return (
		<Flex alignItems="center" justifyContent="center" xcss={containerStyles}>
			<ProjectSvg projectName={projectName ?? ''} activeView="board" injections={injections} />
		</Flex>
	);
};

const containerStyles = xcss({
	height: '100%',
	backgroundColor: 'elevation.surface',
});
